import React, { useContext, useMemo } from "react";

import axiosInstance from "config/axios";
import {
  AccessLevelService,
  AgreementService,
  ApplicationInstanceSettingService,
  ApplicationService,
  AttendanceRecordService,
  AuthenticationV2Service,
  AuthorityService,
  CatalogService,
  CatalogSkuService,
  CatalogSkuTypesService,
  CertificateService,
  ClaimService,
  CompletionService,
  ConfigurationService,
  ContentService,
  EmailService,
  EvaluationService,
  InflectionPointService,
  LoggingService,
  MediaService,
  MenuService,
  OrganizationMenuService,
  MigrationJobService,
  NotificationService,
  OrganizationService,
  OrganizationUserService,
  PartnerService,
  PdfService,
  ProductService,
  ProgressionGateService,
  QualificationService,
  LicenseService,
  LicenseTrackerService,
  LicensureBodyService,
  AuthorityTypeService,
  QuestionService,
  RequestService,
  RequirementService,
  RequirementSetService,
  ResourceLibraryService,
  ResourceService,
  RoleService,
  RusticiCourseService,
  ScormExportedFilesService,
  ScormMetadataService,
  SeriesService,
  SettingsService,
  SiteThemeService,
  SkuDeliverableService,
  SkuService,
  SubmissionService,
  TemplateService,
  TestDataGenerationService,
  TimeZoneService,
  UserCertificateService,
  UserEvaluationService,
  UserService,
  UserSkuService,
  WebinarService,
  WidgetService,
  WizardService,
  ProductDeliverableService,
  ApplicationUserService,
  SkuUserAgreementService,
  AttributeService,
  ContractService,
  UserCredentialService,
  MicroservicesStatusService,
  NotificationsConfigurationsService,
  RedisCacheService,
  StateService,
  LocationTypeService,
  CertificateEntityResyncService,
  UserProfileCredentialService,
  ProfessionalInformationService,
  E2eRegressionTestsService,
  DeveloperDashboardService,
  SiteSettingService,
  WebhooksService,
  DataToolsService,
} from "services/KnowfullyAdminClient";

type GlobalRouteData = {
  homeUrl: () => string;

  // Users

  usersUrl: () => string;
  usersListUrl: () => string;
  userDetailsUrl: (userId?: string) => string;
  userCreateUrl: () => string;
  userEditUrl: (userId?: string) => string;
  impersonateRedirectUrl: (userId?: string) => string;
  bulkEnrollmentUrl: (userId?: string) => string;
  userStartMergeUrl: (mergeFromUserId?: string) => string;
  userConfirmMergeUrl: (
    mergeFromUserId?: string,
    mergeToUserId?: string,
  ) => string;
  userCertificatesToolUrl: (userId?: string) => string;
  userSkuCertificatesToolUrl: (userId?: string, userSkuId?: string) => string;
  userSubmissionsToolUrl: (userId?: string) => string;
  certifyUserCourseUrl: (userId?: string, userSkuId?: string) => string;
  markUserAsAttendedUrl: (userId?: string, userSkuId?: string) => string;
  modifyEnrollmentUrl: (userId?: string, userSkuId?: string) => string;
  userSkuDeleteUrl: (userId?: string, userSkuId?: string) => string;
  processChargebackUrl: (userId?: string, userSkuId?: string) => string;
  viewUserDesignationsChangeLogUrl: (userId?: string) => string;
  userProfileManagementUrl: (userId?: string) => string;
  changeParentUserSkuUrl: (userId?: string, userSkuId?: string) => string;

  gradeExamsUrl: () => string;
  gradeExamDetailsUrl: (examId?: string) => string;
  userCourseUrl: (userId?: string, userSkuId?: string) => string;
  examResultsUrl: (userId?: string, userSkuId?: string) => string;
  examAttemptDetailsUrl: (
    userId?: string,
    userSkuId?: string,
    examAttemptId?: string,
  ) => string;

  // Roles
  rolesUrl: () => string;
  roleDetailsUrl: (roleId?: string) => string;
  roleEditUrl: (roleId?: string) => string;
  roleCreateUrl: (roleId?: string) => string;

  // Content

  contentUrl: () => string;
  scormUrl: () => string;
  deactivateRusticiCoursesUrl: () => string;
  addExportCoursesUrl: () => string;
  exportedScormFilesUrl: () => string;
  generateScormMetadataUrl: () => string;

  contentBlocksUrl: () => string;
  contentBlockCreateUrl: () => string;
  contentBlockEditUrl: (id?: string) => string;
  contentBlockDetailsUrl: (id?: string) => string;

  resourcesUrl: () => string;
  resourceCreateUrl: () => string;
  resourceDetailsUrl: (resourceId?: string) => string;
  resourceSkusUrl: (resourceId?: string) => string;

  productsUrl: () => string;
  productCreateUrl: () => string;
  productEditUrl: (productId?: string) => string;
  productDetailsUrl: (productId?: string) => string;
  skuFormatsManagementUrl: () => string;
  regexValidationsUrl: () => string;
  productVersionsManagementUrl: () => string;

  skusUrl: () => string;
  skuDetailsUrl: (skuId?: string) => string;
  skuRegistrationsUrl: (skuId?: string) => string;
  skuRegistrationsDownloadUrl: (skuId?: string, fileId?: string) => string;
  skuEditUrl: (skuId?: string) => string;
  skuCreateUrl: (productId?: string) => string;

  seriesUrl: () => string;
  seriesDetailsUrl: (seriesId?: string) => string;
  seriesEditUrl: (seriesId?: string) => string;

  settingsUrl: () => string;
  settingsDetailsUrl: (settingId?: string) => string;

  reprocessCompletionsUrl: () => string;

  questionsUrl: () => string;
  questionDetailsUrl: (questionId?: string) => string;
  questionRatingsManagementUrl: () => string;

  evaluationsUrl: () => string;
  evaluationDetailsUrl: (evaluationId?: string) => string;
  evaluationEditUrl: (evaluationId?: string) => string;
  evaluationCreateUrl: () => string;

  // Configuration

  configurationUrl: () => string;

  resourceLibrariesUrl: () => string;
  resourceLibrariesDetailsUrl: (resourceLibraryId?: string) => string;
  resourceLibraryItemDetailsUrl: (
    resourceLibraryId?: string,
    resourceLibraryItemId?: string,
  ) => string;

  organizationsUrl: () => string;
  organizationCreateUrl: () => string;
  organizationDetailsUrl: (organizationId?: string) => string;

  wizardsUrl: () => string;
  wizardCreateUrl: () => string;
  wizardDetailsUrl: (wizardId?: string) => string;

  qualificationsUrl: () => string;
  qualificationDetailsUrl: (qualificationId?: string) => string;
  licensesUrl: () => string;
  licenseDetailsUrl: (licenseId?: string) => string;
  requirementSetDetailsUrl: (requirementSetId?: string) => string;
  requirementDetailsUrl: (requirementId?: string) => string;
  licenseAuthoringUrl: (licenseId?: string) => string;
  qualificationSkusUrl: (qualificationId?: string) => string;
  licensureBodiesUrl: () => string;
  licensureBodyDetailsUrl: (licensureBodyId?: string) => string;
  licensureBodySkusUrl: (licensureBodyId?: string) => string;
  submissionAuthoritiesUrl: () => string;
  submissionAuthorityDetailsUrl: (authorityTypeId?: string) => string;
  submissionAuthoritySkusUrl: (authorityTypeId?: string) => string;

  certificatesUrl: () => string;

  certificateListUrl: () => string;
  certificateEditUrl: (certificateId?: string) => string;
  certificateCreateUrl: () => string;
  certificateDetailsUrl: (certificateId?: string) => string;

  certificateThemesUrl: () => string;
  certificateThemeDetailsUrl: (certificateThemeId?: string) => string;
  certificateThemeEditUrl: (certificateThemeId?: string) => string;
  certificateThemeCreateUrl: () => string;

  certificateTemplatesUrl: () => string;
  certificateTemplateDetailsUrl: (certificateTemplateId?: string) => string;
  certificateTemplateEditUrl: (certificateTemplateId?: string) => string;
  certificateTemplateCreateUrl: () => string;

  userCertificateDeepLink: (userCertificateId?: string) => string;
  userCourseDeepLink: (userSkuId?: string) => string;

  dynamicMenuUrl: () => string;

  contentTemplatesUrl: () => string;

  contentTemplateEditUrl: (templateId?: string) => string;
  contentTemplateCreateUrl: () => string;
  contentTemplateDetailsUrl: (contentTempleteId?: string) => string;

  menusUrl: () => string;
  menuDetailsUrl: (menuId?: string) => string;
  menuCreateUrl: () => string;
  menuEditUrl: (menuId?: string) => string;

  menuItemsUrl: () => string;
  menuItemDetailsUrl: (menuId?: string) => string;
  menuItemCreateUrl: () => string;
  menuItemEditUrl: (menuId?: string) => string;

  operationsUrl: () => string;
  importsUrl: () => string;
  testDataGenerationUrl: () => string;
  klgRequestUrl: () => string;
  importsUploadUrl: () => string;
  importsColumnMappingUrl: (migrationJobTypeId?: string) => string;
  importsProcessUrl: (
    migrationJobTypeId?: string,
    dateString?: string,
  ) => string;
  processesUrl: () => string;
  healthCheckUrl: () => string;

  emailsUrl: () => string;
  emailDetaisUrl: (emailId?: string) => string;
  e2eRegressionTestsUrl: () => string;
  webhooksUrl: () => string;
  dataToolsUrl: () => string;
  scriptRunnerUrl: () => string;
  developerDashboardUrl: () => string;
  dataToolsRunUrl: (runId?: string) => string;

  importsSummaryUrl: (migrationJobId?: string) => string;

  eventsUrl: () => string;
  eventDetailsUrl: (skuId?: string) => string;
  eventAttendanceRecordsUrl: (skuId?: string) => string;
  attendanceRecordsDownloadUrl: (skuId?: string, fileId?: string) => string;

  accreditationUrl: () => string;
  addCreditSubmissionReportsUrl: () => string;
  previouslyExportedReportsUrl: () => string;

  mediaLibraryUrl: () => string;
  createMediaLibraryItemUrl: () => string;
  mediaLibraryItemDetailsUrl: (itemId?: string) => string;

  notificationsUrl: () => string;
  notificationConfigurationsUrl: () => string;
  notificationConfigurationDetailsUrl: (
    notificationJobRoutingId?: string,
  ) => string;
  notificationConfigurationCreateUrl: () => string;
  notificationConfigurationEditUrl: (
    notificationJobRoutingId?: string,
  ) => string;
  emailTemplatesUrl: () => string;
  emailTemplateDetailsUrl: (emailTemplateId?: string) => string;
  emailTemplateCreateUrl: () => string;
  emailTemplateEditUrl: (emailTemplateId?: string) => string;
  emailThemeUrl: () => string;
  emailThemeCreateUrl: () => string;
  emailThemeEditUrl: () => string;

  agreementsUrl: () => string;
  agreementDetailsUrl: (agreementId?: string) => string;
  agreementEditUrl: (agreementId?: string) => string;
  agreementCreateUrl: () => string;

  skuUserAgreementsUrl: () => string;
  skuUserAgreementCreateUrl: () => string;
  skuUserAgreementDetailsUrl: (userAgreementId?: string) => string;

  attributesUrl: () => string;
  attributeCreateUrl: () => string;
  attributeEditUrl: (attributeId?: string) => string;
  attributeDetailsUrl: (attributeId?: string) => string;
  attributeListUrl: (codeConstant?: string) => string;

  unauthorizedUrl: () => string;
  errorUrl: () => string;
};

type GlobalServices = {
  applicationService: ApplicationService;
  applicationUserService: ApplicationUserService;
  authenticationService: AuthenticationV2Service;
  claimService: ClaimService;
  roleService: RoleService;
  userService: UserService;
  loggingService: LoggingService;
  userSkuService: UserSkuService;
  questionService: QuestionService;
  certificateService: CertificateService;
  evaluationService: EvaluationService;
  userEvaluationService: UserEvaluationService;
  productService: ProductService;
  catalogService: CatalogService;
  skuService: SkuService;
  seriesService: SeriesService;
  requestService: RequestService;
  resourceLibraryService: ResourceLibraryService;
  contentService: ContentService;
  templateService: TemplateService;
  timeZoneService: TimeZoneService;
  qualificationService: QualificationService;
  licenseService: LicenseService;
  requirementSetService: RequirementSetService;
  requirementService: RequirementService;
  licenseTrackerService: LicenseTrackerService;
  licensureBodyService: LicensureBodyService;
  submissionAuthorityService: AuthorityTypeService;
  menuService: MenuService;
  organizationMenuService: OrganizationMenuService;
  migrationJobService: MigrationJobService;
  pdfService: PdfService;
  userCertificateService: UserCertificateService;
  webinarService: WebinarService;
  authorityService: AuthorityService;
  submissionService: SubmissionService;
  resourceService: ResourceService;
  wizardService: WizardService;
  inflectionPointService: InflectionPointService;
  widgetService: WidgetService;
  applicationInstanceSettingService: ApplicationInstanceSettingService;
  attendanceRecordService: AttendanceRecordService;
  progressionGateService: ProgressionGateService;
  organizationService: OrganizationService;
  organizationUserService: OrganizationUserService;
  accessLevelService: AccessLevelService;
  agreementService: AgreementService;
  partnerService: PartnerService;
  mediaService: MediaService;
  rusticiCourseService: RusticiCourseService;
  scormExportedFilesService: ScormExportedFilesService;
  scormMetadataService: ScormMetadataService;
  settingService: SettingsService;
  siteThemeService: SiteThemeService;
  catalogSkuService: CatalogSkuService;
  catalogSkuTypeService: CatalogSkuTypesService;
  notificationService: NotificationService;
  notificationsConfigurationsService: NotificationsConfigurationsService;
  testDataGenerationService: TestDataGenerationService;
  configurationService: ConfigurationService;
  completionService: CompletionService;
  skuDeliverableService: SkuDeliverableService;
  productDeliverableService: ProductDeliverableService;
  emailService: EmailService;
  skuUserAgreementService: SkuUserAgreementService;
  contractService: ContractService;
  userCredentialService: UserCredentialService;
  attributeService: AttributeService;
  microservicesStatusService: MicroservicesStatusService;
  redisCacheService: RedisCacheService;
  stateService: StateService;
  locationTypeService: LocationTypeService;
  certificateEntityResyncService: CertificateEntityResyncService;
  userProfileCredentialService: UserProfileCredentialService;
  professionalInformationService: ProfessionalInformationService;
  e2eRegressionTestsService: E2eRegressionTestsService;
  developerDashboardService: DeveloperDashboardService;
  siteSettingService: SiteSettingService;
  webhooksService: WebhooksService;
  dataToolsService: DataToolsService;
};

type GlobalData = GlobalRouteData & GlobalServices;

type GlobalDataProviderProps = {
  children: React.ReactNode;
};

const GlobalDataContext = React.createContext<GlobalData>({} as GlobalData);

const adminClientServices = {
  applicationService: new ApplicationService("", axiosInstance),
  applicationUserService: new ApplicationUserService("", axiosInstance),
  authenticationService: new AuthenticationV2Service("", axiosInstance),
  claimService: new ClaimService("", axiosInstance),
  roleService: new RoleService("", axiosInstance),
  userService: new UserService("", axiosInstance),
  loggingService: new LoggingService("", axiosInstance),
  userSkuService: new UserSkuService("", axiosInstance),
  questionService: new QuestionService("", axiosInstance),
  certificateService: new CertificateService("", axiosInstance),
  evaluationService: new EvaluationService("", axiosInstance),
  userEvaluationService: new UserEvaluationService("", axiosInstance),
  productService: new ProductService("", axiosInstance),
  catalogService: new CatalogService("", axiosInstance),
  skuService: new SkuService("", axiosInstance),
  seriesService: new SeriesService("", axiosInstance),
  requestService: new RequestService("", axiosInstance),
  resourceLibraryService: new ResourceLibraryService("", axiosInstance),
  contentService: new ContentService("", axiosInstance),
  templateService: new TemplateService("", axiosInstance),
  timeZoneService: new TimeZoneService("", axiosInstance),
  qualificationService: new QualificationService("", axiosInstance),
  licenseService: new LicenseService("", axiosInstance),
  requirementSetService: new RequirementSetService("", axiosInstance),
  requirementService: new RequirementService("", axiosInstance),
  licenseTrackerService: new LicenseTrackerService("", axiosInstance),
  licensureBodyService: new LicensureBodyService("", axiosInstance),
  submissionAuthorityService: new AuthorityTypeService("", axiosInstance),
  menuService: new MenuService("", axiosInstance),
  organizationMenuService: new OrganizationMenuService("", axiosInstance),
  migrationJobService: new MigrationJobService("", axiosInstance),
  pdfService: new PdfService("", axiosInstance),
  webinarService: new WebinarService("", axiosInstance),
  userCertificateService: new UserCertificateService("", axiosInstance),
  authorityService: new AuthorityService("", axiosInstance),
  submissionService: new SubmissionService("", axiosInstance),
  resourceService: new ResourceService("", axiosInstance),
  wizardService: new WizardService("", axiosInstance),
  inflectionPointService: new InflectionPointService("", axiosInstance),
  widgetService: new WidgetService("", axiosInstance),
  applicationInstanceSettingService: new ApplicationInstanceSettingService(
    "",
    axiosInstance,
  ),
  attendanceRecordService: new AttendanceRecordService("", axiosInstance),
  progressionGateService: new ProgressionGateService("", axiosInstance),
  organizationService: new OrganizationService("", axiosInstance),
  organizationUserService: new OrganizationUserService("", axiosInstance),
  accessLevelService: new AccessLevelService("", axiosInstance),
  agreementService: new AgreementService("", axiosInstance),
  partnerService: new PartnerService("", axiosInstance),
  mediaService: new MediaService("", axiosInstance),
  rusticiCourseService: new RusticiCourseService("", axiosInstance),
  scormExportedFilesService: new ScormExportedFilesService("", axiosInstance),
  scormMetadataService: new ScormMetadataService("", axiosInstance),
  settingService: new SettingsService("", axiosInstance),
  siteThemeService: new SiteThemeService("", axiosInstance),
  catalogSkuService: new CatalogSkuService("", axiosInstance),
  catalogSkuTypeService: new CatalogSkuTypesService("", axiosInstance),
  notificationService: new NotificationService("", axiosInstance),
  notificationsConfigurationsService: new NotificationsConfigurationsService(
    "",
    axiosInstance,
  ),
  testDataGenerationService: new TestDataGenerationService("", axiosInstance),
  configurationService: new ConfigurationService("", axiosInstance),
  completionService: new CompletionService("", axiosInstance),
  skuDeliverableService: new SkuDeliverableService("", axiosInstance),
  productDeliverableService: new ProductDeliverableService("", axiosInstance),
  emailService: new EmailService("", axiosInstance),
  skuUserAgreementService: new SkuUserAgreementService("", axiosInstance),
  attributeService: new AttributeService("", axiosInstance),
  contractService: new ContractService("", axiosInstance),
  userCredentialService: new UserCredentialService("", axiosInstance),
  microservicesStatusService: new MicroservicesStatusService("", axiosInstance),
  redisCacheService: new RedisCacheService("", axiosInstance),
  stateService: new StateService("", axiosInstance),
  locationTypeService: new LocationTypeService("", axiosInstance),
  certificateEntityResyncService: new CertificateEntityResyncService(
    "",
    axiosInstance,
  ),
  userProfileCredentialService: new UserProfileCredentialService(
    "",
    axiosInstance,
  ),
  professionalInformationService: new ProfessionalInformationService(
    "",
    axiosInstance,
  ),
  e2eRegressionTestsService: new E2eRegressionTestsService("", axiosInstance),
  developerDashboardService: new DeveloperDashboardService("", axiosInstance),
  siteSettingService: new SiteSettingService("", axiosInstance),
  webhooksService: new WebhooksService("", axiosInstance),
  dataToolsService: new DataToolsService("", axiosInstance),
};

const routeUrls = {
  homeUrl: () => "/home",

  // Users

  usersUrl: () => "/users",
  usersListUrl: () => "/users/list",
  userDetailsUrl: (userId?: string) => `/users/list/-/${userId ?? ":id"}`,
  userCreateUrl: () => "/users/list/new",
  userEditUrl: (userId?: string) => `/users/list/edit/${userId ?? ":id"}`,
  impersonateRedirectUrl: (userId?: string) =>
    `/users/impersonate/${userId ?? ":userId"}`,
  bulkEnrollmentUrl: (userId?: string) =>
    userId ? `/users/bulk-enrollment?userId=${encodeURIComponent(userId)}` : "/users/bulk-enrollment",
  userStartMergeUrl: (mergeFromUserId?: string) =>
    `/users/list/${mergeFromUserId ?? ":idFrom"}/merge`,
  userConfirmMergeUrl: (mergeFromUserId?: string, mergeToUserId?: string) =>
    `/users/list/${mergeFromUserId ?? ":idFrom"}/merge/${
      mergeToUserId ?? ":idTo"
    }`,

  gradeExamsUrl: () => "/users/grade-exams",
  gradeExamDetailsUrl: (examId?: string) =>
    `/users/grade-exams/-/${examId ?? ":id"}`,
  userCourseUrl: (userId?: string, userSkuId?: string) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }`,
  examResultsUrl: (userId?: string, userSkuId?: string) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }/tools/exam-results`,
  examAttemptDetailsUrl: (
    userId?: string,
    userSkuId?: string,
    examAttemptId?: string,
  ) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }/tools/exam-results/${examAttemptId ?? ":examAttemptId"}`,
  certifyUserCourseUrl: (userId?: string, userSkuId?: string) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }/tools/certify`,
  markUserAsAttendedUrl: (userId?: string, userSkuId?: string) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }/tools/mark-user-as-attended`,

  processChargebackUrl: (userId?: string, userSkuId?: string) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }/tools/chargeback`,

  modifyEnrollmentUrl: (userId?: string, userSkuId?: string) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }/tools/enrollment`,
  userSkuDeleteUrl: (userId?: string, userSkuId?: string) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }/tools/delete-user-sku`,
  userCertificatesToolUrl: (userId?: string) =>
    `/users/list/${userId ?? ":userId"}/tools/certificates`,
  userSkuCertificatesToolUrl: (userId?: string, userSkuId?: string) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }/tools/certificates`,
  userSubmissionsToolUrl: (userId?: string) =>
    `/users/list/${userId ?? ":userId"}/tools/submissions`,
  viewUserDesignationsChangeLogUrl: (userId?: string) =>
    `/users/list/${userId ?? ":userId"}/tools/user-designations-change-log`,
  userProfileManagementUrl: (userId?: string) =>
    `/users/list/${userId ?? ":userId"}/tools/profile-management`,
  changeParentUserSkuUrl: (userId?: string, userSkuId?: string) =>
    `/users/list/${userId ?? ":userId"}/user-course/${
      userSkuId ?? ":userSkuId"
    }/tools/change-parent`,

  // Content

  contentUrl: () => "/content",

  scormUrl: () => "/content/scorm",

  deactivateRusticiCoursesUrl: () => "/content/scorm/deactivate-rustici",

  addExportCoursesUrl: () => "/content/scorm/add-export-courses",

  exportedScormFilesUrl: () => "/content/scorm/exported-files",

  generateScormMetadataUrl: () => "/content/scorm/generate-metadata",

  contentBlocksUrl: () => "/configuration/content-blocks",
  contentBlockCreateUrl: () => "/configuration/content-blocks/create",
  contentBlockEditUrl: (id?: string) =>
    `/configuration/content-blocks/edit/${id ?? ":id"}`,
  contentBlockDetailsUrl: (id?: string) =>
    `/configuration/content-blocks/-/${id ?? ":id"}`,

  resourcesUrl: () => "/content/resources",
  resourceCreateUrl: () => "/content/resources/new",
  resourceDetailsUrl: (resourceId?: string) =>
    `/content/resources/${resourceId ?? ":id"}`,
  resourceSkusUrl: (resourceId?: string) =>
    `/content/resources/-/${resourceId ?? ":id"}/skus`,

  productsUrl: () => "/content/products",
  productCreateUrl: () => "/content/products/new",
  productEditUrl: (productId?: string) =>
    `/content/products/edit/${productId ?? ":id"}`,
  productDetailsUrl: (productId?: string) =>
    `/content/products/-/${productId ?? ":id"}`,
  productVersionsManagementUrl: () => "/content/products/versions",

  skusUrl: () => "/content/skus",
  skuDetailsUrl: (skuId?: string) => `/content/skus/-/${skuId ?? ":id"}`,
  skuRegistrationsUrl: (skuId?: string) =>
    `/content/skus/-/${skuId ?? ":id"}/registrations`,
  skuRegistrationsDownloadUrl: (skuId?: string, fileId?: string) =>
    `/content/skus/-/${skuId ?? ":id"}/registrations/download/${
      fileId ?? ":fileId"
    }`,
  skuEditUrl: (skuId?: string) => `/content/skus/edit/${skuId ?? ":id"}`,
  skuCreateUrl: (productId?: string) =>
    `/content/skus/new/${productId ?? ":productId"}`,

  seriesUrl: () => "/content/series",
  seriesDetailsUrl: (seriesId?: string) =>
    `/content/series/-/${seriesId ?? ":id"}`,
  seriesEditUrl: (seriesId?: string) =>
    `/content/series/edit/${seriesId ?? ":id"}`,

  evaluationsUrl: () => "/content/evaluations",
  evaluationDetailsUrl: (evaluationId?: string) =>
    `/content/evaluations/-/${evaluationId ?? ":id"}`,
  evaluationEditUrl: (evaluationId?: string) =>
    `/content/evaluations/edit/${evaluationId ?? ":id"}`,
  evaluationCreateUrl: () => "/content/evaluations/new",

  questionsUrl: () => "/content/questions",
  questionRatingsManagementUrl: () => "/content/questions/ratings",
  questionDetailsUrl: (questionId?: string) =>
    `/content/questions/-/${questionId ?? ":id"}`,

  resourceLibrariesUrl: () => "/content/resource-libraries",
  resourceLibrariesDetailsUrl: (resourceLibraryId?: string) =>
    `/content/resource-libraries/-/${resourceLibraryId ?? ":id"}`,
  resourceLibraryItemDetailsUrl: (
    resourceLibraryId?: string,
    resourceLibraryItemId?: string,
  ) =>
    `/content/resource-libraries/${resourceLibraryId ?? ":rid"}/items/-/${
      resourceLibraryItemId ?? ":id"
    }`,

  mediaLibraryUrl: () => "/content/media-library",
  createMediaLibraryItemUrl: () => "/content/media-library/new-media",
  mediaLibraryItemDetailsUrl: (itemId?: string) =>
    `/content/media-library/items/${itemId ?? ":id"}`,

  notificationsUrl: () => "/content/notifications",
  notificationConfigurationsUrl: () => "/content/notifications/configurations",
  notificationConfigurationDetailsUrl: (notificationJobRoutingId?: string) =>
    `/content/notifications/configurations/${
      notificationJobRoutingId ?? ":id"
    }`,
  notificationConfigurationCreateUrl: () =>
    "/content/notifications/configurations/new",
  notificationConfigurationEditUrl: (notificationJobRoutingId?: string) =>
    `/content/notifications/configurations/${
      notificationJobRoutingId ?? ":id"
    }/edit`,
  emailTemplatesUrl: () => "/content/notifications/email-templates",
  emailTemplateDetailsUrl: (emailTemplateId?: string) =>
    `/content/notifications/email-templates/${emailTemplateId ?? ":id"}`,
  emailTemplateCreateUrl: () => "/content/notifications/email-templates/new",
  emailTemplateEditUrl: (emailTemplateId?: string) =>
    `/content/notifications/email-templates/${emailTemplateId ?? ":id"}/edit`,
  emailThemeUrl: () => "/content/notifications/email-themes",
  emailThemeCreateUrl: () => "/content/notifications/email-themes/new",
  emailThemeEditUrl: () => "/content/notifications/email-themes/edit",

  agreementsUrl: () => "/content/agreements",
  agreementDetailsUrl: (agreementId?: string) =>
    `/content/agreements/-/${agreementId ?? ":id"}`,
  agreementEditUrl: (agreementId?: string) =>
    `/content/agreements/edit/${agreementId ?? ":id"}`,
  agreementCreateUrl: () => "/content/agreements/new",

  skuUserAgreementsUrl: () => "/content/agreements/user-agreements",
  skuUserAgreementCreateUrl: () => "/content/agreements/user-agreements/new",
  skuUserAgreementDetailsUrl: (userAgreementId?: string) =>
    `/content/agreements/user-agreements/${userAgreementId ?? ":id"}`,

  attributesUrl: () => "/content/attributes",
  attributeCreateUrl: () => "/content/attributes/new",
  attributeEditUrl: (attributeId?: string) =>
    `/content/attributes/edit/${attributeId ?? ":id"}`,
  attributeDetailsUrl: (attributeId?: string) =>
    `/content/attributes/${attributeId ?? ":id"}`,

  attributeListUrl: (codeConstant?: string) =>
    `/content/attributes/by-type/${codeConstant ?? ":code"}`,

  // Configuration

  configurationUrl: () => "/configuration",

  dynamicMenuUrl: () => "/configuration/dynamic-menu",
  menusUrl: () => "/configuration/dynamic-menu/menus",
  menuDetailsUrl: (menuId?: string) =>
    `/configuration/dynamic-menu/menus/-/${menuId ?? ":id"}`,
  menuCreateUrl: () => "/configuration/dynamic-menu/menus/new",
  menuEditUrl: (menuId?: string) =>
    `/configuration/dynamic-menu/menus/edit/${menuId ?? ":id"}`,

  menuItemsUrl: () => "/configuration/dynamic-menu/menu-items",
  menuItemDetailsUrl: (menuItemId?: string) =>
    `/configuration/dynamic-menu/menu-items/-/${menuItemId ?? ":id"}`,
  menuItemCreateUrl: () => "/configuration/dynamic-menu/menu-items/new",
  menuItemEditUrl: (menuItemId?: string) =>
    `/configuration/dynamic-menu/menu-items/edit/${menuItemId ?? ":id"}`,

  contentTemplatesUrl: () => "/configuration/templates",
  contentTemplateDetailsUrl: (contentTempleteId?: string) =>
    `/configuration/templates/-/${contentTempleteId ?? ":id"}`,
  contentTemplateEditUrl: (contentTempleteId?: string) =>
    `/configuration/templates/edit/${contentTempleteId ?? ":id"}`,
  contentTemplateCreateUrl: () => "/configuration/templates/new",

  wizardsUrl: () => "/configuration/wizards",
  wizardCreateUrl: () => "/configuration/wizards/new",
  wizardDetailsUrl: (wizardId?: string) =>
    `/configuration/wizards/${wizardId ?? ":id"}`,

  organizationsUrl: () => "/configuration/organizations",
  organizationCreateUrl: () => "/configuration/organizations/new",
  organizationDetailsUrl: (organizationId?: string) =>
    `/configuration/organizations/-/${organizationId ?? ":organizationId"}`,

  rolesUrl: () => "/configuration/roles",
  roleDetailsUrl: (roleId?: string) =>
    `/configuration/roles/-/${roleId ?? ":id"}`,
  roleEditUrl: (roleId?: string) =>
    `/configuration/roles/edit/${roleId ?? ":id"}`,
  roleCreateUrl: () => `/configuration/roles/new`,

  skuFormatsManagementUrl: () => "/configuration/sku-formats",
  regexValidationsUrl: () => "/configuration/regex-validations",

  settingsUrl: () => "/configuration/settings",
  settingsDetailsUrl: (settingId?: string) =>
    `/configuration/settings/-/${settingId ?? ":id"}`,

  reprocessCompletionsUrl: () => "/configuration/reprocess-completions",

  qualificationsUrl: () => "/accreditation/qualifications",
  qualificationDetailsUrl: (qualificationId?: string) =>
    `/accreditation/qualifications/-/${qualificationId ?? ":id"}`,

  licensesUrl: () => "/accreditation/licenses",
  licenseDetailsUrl: (licenseId?: string) =>
    `/accreditation/licenses/-/${licenseId ?? ":id"}`,
  requirementSetDetailsUrl: (requirementSetId?: string) =>
    `/accreditation/requirement-sets/-/${requirementSetId ?? ":id"}`,
  requirementDetailsUrl: (requirementId?: string) =>
    `/accreditation/requirements/-/${requirementId ?? ":id"}`,
  licenseAuthoringUrl: (licenseId?: string) =>
    `/accreditation/licenses/-/${licenseId ?? ":licenseId"}/author`,
  qualificationSkusUrl: (qualificationId?: string) =>
    `/accreditation/qualifications/-/${qualificationId ?? ":id"}/skus`,

  licensureBodiesUrl: () => "/accreditation/licensure-bodies",
  licensureBodyDetailsUrl: (licensureBodyId?: string) =>
    `/accreditation/licensure-bodies/-/${licensureBodyId ?? ":id"}`,
  licensureBodySkusUrl: (licensureBodyId?: string) =>
    `/accreditation/licensure-bodies/-/${licensureBodyId ?? ":id"}/skus`,

  submissionAuthoritiesUrl: () => "/accreditation/submission-authorities",
  submissionAuthorityDetailsUrl: (authorityTypeId?: string) =>
    `/accreditation/submission-authorities/-/${authorityTypeId ?? ":id"}`,
  submissionAuthoritySkusUrl: (authorityTypeId?: string) =>
    `/accreditation/submission-authorities/-/${authorityTypeId ?? ":id"}/skus`,

  certificatesUrl: () => "/certificates",

  certificateListUrl: () => "/certificates/certificates",
  certificateCreateUrl: () => "/certificates/certificates/new",
  certificateDetailsUrl: (certificateId?: string) =>
    `/certificates/certificates/-/${certificateId ?? ":id"}`,
  certificateEditUrl: (certificateId?: string) =>
    `/certificates/certificates/edit/${certificateId ?? ":id"}`,

  certificateThemesUrl: () => "/certificates/themes",
  certificateThemeDetailsUrl: (certificateThemeId?: string) =>
    `/certificates/themes/-/${certificateThemeId ?? ":id"}`,
  certificateThemeCreateUrl: () => "/certificates/themes/new",
  certificateThemeEditUrl: (certificateThemeId?: string) =>
    `/certificates/themes/edit/${certificateThemeId ?? ":id"}`,

  certificateTemplatesUrl: () => "/certificates/templates",
  certificateTemplateDetailsUrl: (certificateTemplateId?: string) =>
    `/certificates/templates/-/${certificateTemplateId ?? ":id"}`,
  certificateTemplateCreateUrl: () => "/certificates/templates/new",
  certificateTemplateEditUrl: (certificateTemplateId?: string) =>
    `/certificates/templates/edit/${certificateTemplateId ?? ":id"}`,

  userCertificateDeepLink: (userCertificateId?: string) =>
    `/users/user-certificates/${userCertificateId ?? ":userCertificateId"}`,
  userCourseDeepLink: (userSkuId?: string) =>
    `/users/user-courses/${userSkuId ?? ":userSkuId"}`,

  operationsUrl: () => "/operations",
  importsUrl: () => "/operations/imports",
  testDataGenerationUrl: () => "/operations/test-data-generation",
  klgRequestUrl: () => "/operations/requests",
  importsUploadUrl: () => "/operations/imports/new",
  importsColumnMappingUrl: (migrationJobTypeId?: string) =>
    `/operations/imports/${migrationJobTypeId ?? ":id"}/mapping`,
  importsProcessUrl: (migrationJobTypeId?: string, dateString?: string) =>
    `/operations/imports/${migrationJobTypeId ?? ":id"}/process/${
      dateString ?? ":dateString"
    }`,
  importsSummaryUrl: (migrationJobId?: string) =>
    `/operations/imports/summary/${migrationJobId ?? ":jobId"}`,
  processesUrl: () => "/operations/processes",
  healthCheckUrl: () => "/operations/health-check",

  emailsUrl: () => "/operations/emails",
  e2eRegressionTestsUrl: () => "/operations/e2e-regression-tests",
  webhooksUrl: () => "/operations/webhooks",
  dataToolsUrl: () => "/operations/data-tools",
  scriptRunnerUrl: () => "/operations/script-runner",
  developerDashboardUrl: () => "/operations/developer-dashboard",
  dataToolsRunUrl: (runId?: string) => `/operations/data-tools/${runId ?? ":runId"}`,
  emailDetaisUrl: (emailId?: string) =>
    `/operations/emails/${emailId ?? ":emailId"}`,

  eventsUrl: () => "/events",
  eventDetailsUrl: (skuId?: string) => `/events/skus/-/${skuId ?? ":id"}`,
  eventAttendanceRecordsUrl: (skuId?: string) =>
    `/events/skus/-/${skuId ?? ":id"}/organization/attendance-records`,
  attendanceRecordsDownloadUrl: (skuId?: string, fileId?: string) =>
    `/events/skus/-/${skuId ?? ":id"}/attendance-records/download/${
      fileId ?? ":fileId"
    }`,

  accreditationUrl: () => "/accreditation",
  addCreditSubmissionReportsUrl: () =>
    "/accreditation/credit-submission-reports/add",
  previouslyExportedReportsUrl: () =>
    "/accreditation/credit-submission-reports",

  unauthorizedUrl: () => "/unauthorized",
  errorUrl: () => "/error",
};

export const GlobalDataProvider: React.FC<GlobalDataProviderProps> = ({
  children,
}: GlobalDataProviderProps) => {
  // This allows us to fetch a URL from any component using useGlobalContext()
  // We use functions in a context so that we can easily provide for internationalized URLs (/en/...), should we ever need to, based on the
  // current user's preferences

  const globalData = useMemo(
    () => ({ ...routeUrls, ...adminClientServices }),
    [],
  );

  return (
    <GlobalDataContext.Provider value={globalData}>
      {children}
    </GlobalDataContext.Provider>
  );
};

const useGlobalContext = () => useContext<GlobalData>(GlobalDataContext);

export default useGlobalContext;
